import { cn } from '@/lib/utils';
import { useId    } from 'react';
import type {InputHTMLAttributes, ReactNode, TextareaHTMLAttributes} from 'react';

/**
 * Champ de formulaire : label, contrôle, aide et message d'erreur.
 *
 * Le composant génère les identifiants et les liens ARIA (`aria-describedby`,
 * `aria-invalid`) : un message d'erreur affiché ici est réellement annoncé par
 * un lecteur d'écran, pas seulement coloré en rouge.
 *
 * Les erreurs affichées proviennent de `fieldErrors()` (voir `lib/errors.ts`),
 * qui extrait le détail champ par champ d'un `422 validation_failed`. La clé
 * du champ correspond au nom attendu par l'API : `email`, `password`,
 * `card_last4`… ce qui permet de brancher les deux sans table de conversion.
 */

const CONTROL_CLASSES =
    'w-full rounded-lg border border-line-strong bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-muted/70 transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:bg-surface-sunken disabled:text-ink-muted';

const CONTROL_INVALID = 'border-critical focus:border-critical focus:ring-critical/20';

type FieldShellProps = {
    label: string;
    /** Message d'erreur, généralement issu de `fieldErrors(error)[nom]`. */
    error?: string;
    /** Aide affichée sous le champ tant qu'il n'y a pas d'erreur. */
    hint?: ReactNode;
    /** Repère visuel discret pour les champs facultatifs. */
    optional?: boolean;
    className?: string;
    children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
};

/**
 * Enveloppe générique : c'est elle qui porte toute la logique d'accessibilité.
 * `Input` et `Textarea` ci-dessous n'en sont que des raccourcis.
 */
export function Field({ label, error, hint, optional, className, children }: FieldShellProps) {
    const id = useId();
    const hintId = `${id}-hint`;
    const errorId = `${id}-error`;
    const describedBy = error ? errorId : hint ? hintId : undefined;

    return (
        <div className={cn('space-y-1.5', className)}>
            <label htmlFor={id} className="text-ink flex items-baseline gap-2 text-sm font-medium">
                {label}
                {optional && <span className="text-ink-muted text-xs font-normal">facultatif</span>}
            </label>

            {children({ id, describedBy, invalid: Boolean(error) })}

            {error ? (
                <p id={errorId} role="alert" className="text-critical text-xs">
                    {error}
                </p>
            ) : hint ? (
                <p id={hintId} className="text-ink-muted text-xs">
                    {hint}
                </p>
            ) : null}
        </div>
    );
}

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & {
    label: string;
    error?: string;
    hint?: ReactNode;
    optional?: boolean;
    wrapperClassName?: string;
};

export function Input({ label, error, hint, optional, wrapperClassName, className, ...props }: InputProps) {
    return (
        <Field label={label} error={error} hint={hint} optional={optional} className={wrapperClassName}>
            {({ id, describedBy, invalid }) => (
                <input
                    {...props}
                    id={id}
                    aria-describedby={describedBy}
                    aria-invalid={invalid || undefined}
                    className={cn(CONTROL_CLASSES, invalid && CONTROL_INVALID, className)}
                />
            )}
        </Field>
    );
}

type TextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> & {
    label: string;
    error?: string;
    hint?: ReactNode;
    optional?: boolean;
    wrapperClassName?: string;
};

export function Textarea({ label, error, hint, optional, wrapperClassName, className, ...props }: TextareaProps) {
    return (
        <Field label={label} error={error} hint={hint} optional={optional} className={wrapperClassName}>
            {({ id, describedBy, invalid }) => (
                <textarea
                    {...props}
                    id={id}
                    aria-describedby={describedBy}
                    aria-invalid={invalid || undefined}
                    className={cn(CONTROL_CLASSES, 'min-h-32 resize-y', invalid && CONTROL_INVALID, className)}
                />
            )}
        </Field>
    );
}

/**
 * Champ « pot de miel » (honeypot).
 *
 * Invisible pour un humain, rempli par la plupart des robots de spam. Le
 * formulaire de contact s'abstient d'envoyer quoi que ce soit si ce champ
 * porte une valeur. L'API applique de son côté sa propre protection : les
 * deux sont complémentaires.
 */
export function HoneypotField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
    return (
        <div aria-hidden className="absolute -left-[9999px] h-px w-px overflow-hidden">
            <label htmlFor="company-website">Ne remplissez pas ce champ</label>
            <input
                id="company-website"
                name="company_website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        </div>
    );
}
